import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Target, 
  BarChart3, 
  Users, 
  ArrowRight,
  Sparkles,
  Brain,
  TrendingUp 
} from 'lucide-react';

interface WelcomeStepProps {
  onNext: () => void;
  className?: string;
}

export const WelcomeStep: React.FC<WelcomeStepProps> = ({
  onNext,
  className = '',
}) => {
  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Targeting',
      description: 'Our LLM processes your natural language descriptions to identify perfect prospects',
      color: 'text-primary-500',
      bgColor: 'bg-primary-50',
    },
    {
      icon: Target,
      title: 'Precision Outreach',
      description: 'Reach exactly the right people with personalized messaging at scale',
      color: 'text-secondary-500',
      bgColor: 'bg-secondary-50',
    },
    {
      icon: TrendingUp,
      title: 'Superior Results',
      description: 'Achieve 3x higher response rates compared to traditional outreach methods',
      color: 'text-warning-500',
      bgColor: 'bg-warning-50',
    },
  ];

  const stats = [
    { value: '3x', label: 'Higher Response Rates' },
    { value: '85%', label: 'Time Saved' },
    { value: '50k+', label: 'Successful Campaigns' },
    { value: '95%', label: 'Customer Satisfaction' },
  ];

  return (
    <div className={`max-w-4xl mx-auto px-6 py-12 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        {/* Hero section */}
        <div className="relative mb-8">
          <motion.div
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              repeatType: 'reverse'
            }}
            className="absolute -top-4 -right-4 text-warning-400"
          >
            <Sparkles className="w-8 h-8" />
          </motion.div>
          
          <h1 className="text-5xl font-bold text-neutral-900 font-display mb-6">
            Welcome to{' '}
            <span className="gradient-text">
              Bombard AI
            </span>
          </h1>
          
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            The intelligent outreach platform that transforms how you find and connect with your ideal customers. 
            Let's build your first AI-powered campaign.
          </p>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-soft border border-neutral-200 hover:shadow-medium transition-all duration-300"
            >
              <div className="text-3xl font-bold text-primary-600 mb-2">{stat.value}</div>
              <div className="text-sm text-neutral-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Features grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="grid md:grid-cols-3 gap-8 mb-12"
      >
        {features.map((feature, index) => (
          <motion.div
            key={feature.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-secondary-500/5 rounded-2xl transform group-hover:scale-105 transition-transform duration-300" />
            
            <div className="relative bg-white p-8 rounded-2xl shadow-soft border border-neutral-200 group-hover:shadow-medium transition-all duration-300">
              <div className={`inline-flex p-3 rounded-xl ${feature.bgColor} mb-6`}>
                <feature.icon className={`w-6 h-6 ${feature.color}`} />
              </div>
              
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">
                {feature.title}
              </h3>
              
              <p className="text-neutral-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Process overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl p-8 mb-12"
      >
        <h2 className="text-2xl font-bold text-neutral-900 text-center mb-8">
          How It Works
        </h2>
        
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { step: 1, title: 'Company Setup', description: 'Tell us about your business' },
            { step: 2, title: 'Define Targets', description: 'Describe your ideal customers' },
            { step: 3, title: 'Set Goals', description: 'Choose your campaign objectives' },
            { step: 4, title: 'Launch & Scale', description: 'AI optimizes your outreach' },
          ].map((item, index) => (
            <div key={item.step} className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-500 text-white rounded-full text-lg font-bold mb-4">
                {item.step}
              </div>
              <h3 className="font-semibold text-neutral-900 mb-2">{item.title}</h3>
              <p className="text-sm text-neutral-600">{item.description}</p>
              
              {index < 3 && (
                <div className="hidden md:block absolute top-6 left-full w-full">
                  <ArrowRight className="w-5 h-5 text-neutral-300 mx-auto" />
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* CTA section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="text-center"
      >
        <div className="bg-white rounded-2xl p-8 shadow-soft border border-neutral-200">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-6 h-6 text-warning-500" />
            <span className="text-lg font-semibold text-neutral-900">
              Ready to get started?
            </span>
          </div>
          
          <p className="text-neutral-600 mb-8 max-w-md mx-auto">
            This intelligent setup takes about 10 minutes and will create a personalized outreach strategy for your business.
          </p>
          
          <motion.button
            onClick={onNext}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 
                     text-white font-semibold rounded-xl shadow-glow-primary hover:shadow-strong transition-all duration-300"
          >
            <span>Start Your Intelligent Setup</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
          
          <p className="text-xs text-neutral-500 mt-4">
            No credit card required • Setup takes ~10 minutes
          </p>
        </div>
      </motion.div>
    </div>
  );
}; 